// Arb ladder: the ordinary Panel fed by ArbEngine, with two leg rows in place of the search bar.

import { type ArbEngine, type ArbLeg, type ArbLegs, arbTicker } from '../arb/engine';
import type { RealVenueId, VenueEngine } from '../venue/engine';
import { Panel, type PanelHost } from './panel';
import { escHtml } from './toast';

export interface ArbHost {
  arb: ArbEngine;
  engineFor(venue: RealVenueId): VenueEngine;
  /** Venues a leg may sit on: those with a key loaded. */
  venues(): { id: RealVenueId; label: string }[];
  accounts: PanelHost['accounts'];
  /** A leg or the layout changed. */
  onChange(): void;
}

export interface ArbLegsPartial {
  q: ArbLeg | null;
  h: ArbLeg | null;
}

type Side = 'q' | 'h';

/** A leg quiet for this long is marked stale. */
const STALE_MS = 20_000;

export class ArbPanel {
  readonly el: HTMLDivElement;
  readonly venue = 'arb' as const;
  readonly panel: Panel;
  legs: ArbLegsPartial = { q: null, h: null };

  private timers: Record<Side, number | undefined> = { q: undefined, h: undefined };
  private offSnapshot: () => void;
  private stateTimer: number;
  /** Whether a click on this ladder may cross. Off: resting prices only, and every click rests a quote. */
  crossing: boolean;

  constructor(readonly id: string, private host: ArbHost, saved?: ArbLegsPartial, crossing = true) {
    this.crossing = crossing;
    this.el = document.createElement('div');
    this.el.className = 'arb-wrap';
    this.el.innerHTML = `<div class="p-search arb-legs">${this.legRow('q', 'Quoter', 'Where our resting orders would sit')}${this.legRow('h', 'Hedger', 'The book we would cross into')}<div class="arb-opts"><button class="arb-cross js-cross" type="button"></button></div><div class="arb-exposure js-exposure" hidden></div></div>`;
    this.panel = new Panel(id, {
      engine: host.arb,
      accounts: host.accounts,
      panels: () => [],
      isStale: () => false,
      isStarred: () => false,
      toggleStar: () => undefined,
      onTickerChange: () => host.onChange(),
      crossing: () => this.crossing,
    }, 'ladder');
    this.el.appendChild(this.panel.el);
    this.refreshVenues(saved);
    this.bind();
    this.renderCrossing();
    this.offSnapshot = host.arb.on('snapshot', (msg) => {
      if (msg.ticker === this.panel.ticker) {
        this.panel.onSnapshot(msg);
        this.renderStates();
      }
    });
    if (saved?.q) this.setLeg('q', saved.q, false);
    if (saved?.h) this.setLeg('h', saved.h, false);
    this.stateTimer = window.setInterval(() => this.renderStates(), 5_000);
  }

  private q<T extends Element>(sel: string): T {
    return this.el.querySelector(sel) as T;
  }

  /** What the grid bar shows. */
  title(): string {
    const { q, h } = this.legs;
    if (!q && !h) return 'empty';
    return `${q?.ticker ?? '?'} − ${h?.ticker ?? '?'}`;
  }

  /** Rebuilds venue dropdowns; a leg's already-set venue stays listed even if its key goes missing. */
  refreshVenues(saved?: ArbLegsPartial): void {
    const avail = this.host.venues();
    for (const side of ['q', 'h'] as Side[]) {
      const sel = this.q<HTMLSelectElement>(`.js-venue-${side}`);
      const current = (this.legs[side] ?? saved?.[side])?.venue ?? (sel.value as RealVenueId | '');
      const opts = [...avail];
      if (current && !opts.some((v) => v.id === current)) opts.push({ id: current, label: `${current} (no key)` });
      const sig = opts.map((v) => v.id).join(',');
      if (sel.dataset.sig !== sig) {
        sel.innerHTML = opts.map((v) => `<option value="${v.id}">${escHtml(v.label)}</option>`).join('');
        sel.dataset.sig = sig;
      }
      if (current) sel.value = current;
    }
  }

  private legRow(side: Side, label: string, hint: string): string {
    return `
  <div class="arb-leg-row" data-side="${side}">
    <span class="arb-lbl" title="${hint}">${label}</span>
    <select class="arb-venue js-venue-${side}"></select>
    <input class="arb-input js-input-${side}" type="text" autocomplete="off" spellcheck="false" placeholder="ticker or search… (Enter to set)"/>
    <span class="arb-state js-state-${side}"></span>
    <button class="p-clear js-clear-${side}" title="Clear leg" style="display:none">✕</button>
    <div class="srch-drop js-drop-${side}"></div>
  </div>`;
  }

  private bind(): void {
    for (const side of ['q', 'h'] as Side[]) {
      const input = this.q<HTMLInputElement>(`.js-input-${side}`);
      const drop = this.q<HTMLElement>(`.js-drop-${side}`);
      const sel = this.q<HTMLSelectElement>(`.js-venue-${side}`);
      input.addEventListener('input', () => {
        clearTimeout(this.timers[side]);
        this.timers[side] = window.setTimeout(() => void this.search(side, input.value.trim(), drop), 300);
      });
      input.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' && input.value.trim()) {
          this.setLeg(side, { venue: sel.value as RealVenueId, ticker: input.value.trim() });
          drop.style.display = 'none';
        }
        if (ev.key === 'Escape') drop.style.display = 'none';
      });
      input.addEventListener('blur', () => { setTimeout(() => { drop.style.display = 'none'; }, 150); });
      sel.addEventListener('change', () => {
        const cur = this.legs[side];
        if (cur) this.setLeg(side, { venue: sel.value as RealVenueId, ticker: cur.ticker });
      });
      this.q(`.js-clear-${side}`).addEventListener('click', () => this.setLeg(side, null));
    }
    this.q('.js-cross').addEventListener('click', () => this.setCrossing(!this.crossing));
  }

  /** Crossing on or off: the ladder repaints from its last frame, and the choice is saved with the layout. */
  setCrossing(on: boolean): void {
    this.crossing = on;
    this.renderCrossing();
    this.panel.refresh();
    this.host.onChange();
  }

  private renderCrossing(): void {
    const b = this.q<HTMLButtonElement>('.js-cross');
    b.classList.toggle('on', this.crossing);
    b.textContent = this.crossing ? 'Crossing on' : 'Crossing off';
    b.title = this.crossing
      ? 'Cross prices shown: a click at one takes both legs now. Click to hide them and only ever rest quotes.'
      : 'Resting prices only: every click rests a quote, nothing crosses. Click to show cross prices and allow crossing.';
  }

  private async search(side: Side, q: string, drop: HTMLElement): Promise<void> {
    if (q.length < 2) {
      drop.style.display = 'none';
      return;
    }
    const venue = this.q<HTMLSelectElement>(`.js-venue-${side}`).value as RealVenueId;
    let markets: { ticker: string; title: string }[];
    try {
      markets = await this.host.engineFor(venue).searchMarkets(q);
    } catch {
      markets = [];
    }
    if (!markets.length) {
      drop.style.display = 'none';
      return;
    }
    drop.innerHTML = markets.map((m) => `
      <div class="srch-item" data-ticker="${escHtml(m.ticker)}">
        <span class="si-tk">${escHtml(m.ticker)}</span>
        <span class="si-title">${escHtml(m.title)}</span>
      </div>`).join('');
    drop.querySelectorAll<HTMLElement>('.srch-item').forEach((item) => {
      item.addEventListener('mousedown', () => {
        this.setLeg(side, { venue, ticker: item.dataset.ticker! });
        drop.style.display = 'none';
      });
    });
    drop.style.display = 'block';
  }

  setLeg(side: Side, leg: ArbLeg | null, notify = true): void {
    this.legs[side] = leg;
    const input = this.q<HTMLInputElement>(`.js-input-${side}`);
    const sel = this.q<HTMLSelectElement>(`.js-venue-${side}`);
    input.value = leg?.ticker ?? '';
    if (leg) sel.value = leg.venue;
    this.q<HTMLElement>(`.js-clear-${side}`).style.display = leg ? '' : 'none';
    // The Panel subscribes to the pair as one ticker; half a pair is nothing.
    const { q, h } = this.legs;
    const old = this.panel.ticker;
    if (q && h) {
      const t = arbTicker({ q, h } as ArbLegs);
      if (old !== t) {
        // Must not leave orders quoting against legs this panel no longer shows.
        if (old) void this.host.arb.cancelAllOrders(old);
        this.panel.select(t);
      }
    } else if (old) {
      void this.host.arb.cancelAllOrders(old);
      this.panel.clearTicker();
    }
    this.renderStates();
    if (notify) this.host.onChange();
  }

  private renderStates(): void {
    const ages = this.panel.ticker ? this.host.arb.legAges(this.panel.ticker) : { q: null, h: null };
    for (const side of ['q', 'h'] as Side[]) {
      const el = this.q<HTMLElement>(`.js-state-${side}`);
      const leg = this.legs[side];
      const age = ages[side];
      const text = !leg ? '' : !this.panel.ticker ? 'waiting for other leg' : age === null ? 'waiting…' : age > STALE_MS ? 'stale' : 'live';
      el.textContent = text;
      el.style.color = text === 'live' ? 'var(--ok)' : text === 'stale' ? 'var(--bad)' : 'var(--dim2)';
    }
    // Unhedged contracts on this pair's quoter leg: a red chip under the legs until they are flat.
    const exp = this.panel.ticker ? this.host.arb.exposure().filter((x) => x.pair === this.panel.ticker) : [];
    const chip = this.q<HTMLElement>('.js-exposure');
    chip.hidden = exp.length === 0;
    chip.textContent = exp.length
      ? `UNHEDGED ${exp.map((x) => `${x.unhedgedMq / 1000} ${x.side === 'bid' ? 'long' : 'short'} ${x.leg.ticker}`).join(', ')} — flatten by hand`
      : '';
  }

  onResized(): void {
    this.panel.onResized();
  }

  destroy(): void {
    clearInterval(this.stateTimer);
    this.offSnapshot();
    if (this.panel.ticker) this.panel.clearTicker();
  }
}
